import Grid from '@/components/atoms/legacy/Grid';
import Heading, { HeadingLevels } from '@/components/atoms/legacy/Heading';
import Wrapper from '@/components/atoms/legacy/Wrapper';
import Breadcrumb from '@/components/molecules/Breadcrumb';
import PageHeader from '@/components/organisms/PageHeader';
import scrollIntoView from '@/lib/scrollIntoView';
import React, { useMemo } from 'react';

const CookiePolicyTemplate: React.FC = () => {
  const p1El = React.useRef(null);
  const p2El = React.useRef(null);
  const p3El = React.useRef(null);
  const p4El = React.useRef(null);

  const nav = useMemo(
    () => [
      {
        text: '1. Summary',
        el: p1El,
      },
      {
        text: '2. Cookies, web beacons, and similar technologies',
        el: p2El,
      },
      {
        text: '3. Your choice and our use of cookies, web beacons, and similar technologies:',
        el: p3El,
      },
      {
        text: '4. Use of these technologies by authorized third-party service providers:',
        el: p4El,
      },
    ],
    []
  );

  return (
    <>
      <Wrapper className="mb-[8px]">
        <Breadcrumb
          links={[
            {
              text: 'Home',
              href: '/',
            },
            {
              text: 'Cookie Policy',
              href: '/cookie_policy',
            },
          ]}
        />
      </Wrapper>
      <Wrapper className="mb-[20px]">
        <PageHeader label="Cookie Policy" />
      </Wrapper>
      <Wrapper className="bg-white pt-[20px]">
        <Grid>
          <section className="col-span-full l:col-span-3">
            <nav className="mb-[60px] bg-blue-light p-[20px] l:sticky l:top-0">
              {nav.map(({ text, el }) => (
                <button
                  key={text}
                  className="w-full p-[10px] text-left text-bodyBold4 text-neutral-800"
                  onClick={() => {
                    if (el.current) {
                      scrollIntoView(el.current);
                    }
                  }}
                >
                  {text}
                </button>
              ))}
            </nav>
          </section>
          <article className="col-span-full l:col-span-8 l:col-start-5">
            <section ref={p1El} className="mb-[60px]">
              <Heading
                className="mb-[10px] text-h4Regular"
                level={HeadingLevels.H2}
              >
                1. Summary
              </Heading>
              <p className="text-microNormal text-neutral-800">
                Our authorized service providers or we may use cookies, web
                beacons, and similar technologies to store information for a
                better, faster, and safer experience, as well as for advertising
                purposes, when you interact with our sites, services,
                applications, tools, or messaging. We provide this page to help
                you understand more about these technologies and our use of them
                in our services, applications, and tools. The following is a
                summary of important things to know about our use of these
                technologies, and you can read the full User Cookie Notice for
                more details.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Our cookies and similar technologies have various functions,
                including necessary functions for our services, improved
                performance, additional functionalities, and relevant and
                targeted ads. We use both session cookies that remain on your
                device as long as you keep your browser active and persistent
                cookies that remain on your device for a longer period. If your
                device permits, you are free to block, delete, or disable these
                technologies. You can manage your cookies and preferences in
                your browser or device settings.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                We have put security measures in place to prevent unauthorized
                access to our cookies and similar technologies, with a unique
                identifier ensuring that only authorized service providers or we
                have access to cookie data.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Our service providers help us with various aspects of our
                business, including site operations, services, applications,
                advertisements, and tools. We use some authorized service
                providers to serve you relevant ads on our services and other
                places on the internet. These service providers may place
                cookies on your device via our services, including third-party
                cookies, and collect information to identify your device, such
                as IP-address or unique device identifiers.
              </p>
            </section>
            <section ref={p2El} className="mb-[60px]">
              <Heading
                className="mb-[10px] text-h4Regular"
                level={HeadingLevels.H2}
              >
                2. Cookies, web beacons, and similar technologies
              </Heading>
              <p className="text-microNormal text-neutral-800">
                When you visit or interact with our sites, services,
                applications, messaging, and tools, we use various technologies
                such as cookies, web beacons, and similar technologies. These
                are small data files that are placed on your computer, tablet,
                mobile phone, or other devices. These technologies allow us to
                record certain pieces of information to improve your experience
                and track user behavior.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                The types and names of the cookies, web beacons, and other
                similar technologies that we use may vary over time. Here are
                some definitions to help you better understand these
                technologies:
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Cookies are small text files that are placed in the memory of
                your browser or device. They allow a website to recognize a
                particular device or browser. There are different types of
                cookies, including session cookies, persistent cookies,
                first-party cookies, and third-party cookies. Cookies can be
                disabled or removed using the tools provided in most commercial
                browsers.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Web beacons are small graphic images that work with cookies to
                identify our users and user behavior. They may be included on
                our sites, services, applications, messaging, and tools.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Similar technologies store information in your browser or device
                using local shared objects or local storage, such as flash
                cookies and HTML 5 cookies. These technologies can operate
                across all of your browsers and may require management directly
                through your installed applications or device.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                We may use the terms &quot;cookies&quot; or &quot;similar
                technologies&quot; interchangeably in our policies. We do not
                use these technologies for storing information to target
                advertising to you on or off our sites.
              </p>
            </section>
            <section ref={p3El} className="mb-[60px]">
              <Heading
                className="mb-[10px] text-h4Regular"
                level={HeadingLevels.H2}
              >
                3. Your choice and our use of cookies, web beacons, and similar
                technologies:
              </Heading>
              <p className="text-microNormal text-neutral-800">
                We provide certain site features, services, applications, and
                tools that can only be accessed by using cookies, web beacons,
                and similar technologies. If your browser, device, or installed
                application allows, you can block, delete, or disable these
                technologies. However, declining these technologies may prevent
                you from using certain site features, services, applications, or
                tools, and require you to frequently re-enter your password
                during your browsing session. For more information on how to
                manage these technologies, please refer to your browser or
                device settings.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Typically, these technologies enable our sites, services,
                applications, and tools to store relevant information on your
                browser or device, which can later be used to identify you to
                our servers or internal systems. We assign a unique identifier
                to our cookies and other similar technologies to protect them
                from unauthorized access and interpretation by anyone other than
                us or our authorized service providers. We never store your
                personal information on any of our cookies or similar
                technologies.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                We obtain your consent to collect and store any personal
                information through these technologies by providing transparent
                notice of their use and giving you the choice to disable them,
                as described above.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Our uses of such technologies fall into the following general
                categories:
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                We use various technologies, such as cookies, web beacons, and
                similar tools, for different purposes, which can be classified
                into four general categories:
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Operationally Necessary: We use these technologies to ensure
                that our sites, services, applications, and tools function
                properly and to prevent fraudulent activities. They also enable
                you to access our services and tools, and use features such as
                shopping carts and saved searches.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Performance Related: We use these technologies to assess the
                performance of our websites, applications, services, and tools.
                This helps us understand how visitors use our websites, and
                determine the effectiveness of our content, applications,
                services, and tools.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Functionality Related: We use these technologies to offer you
                enhanced functionality when accessing or using our sites,
                services, applications, or tools. For example, we may use them
                to identify you when you sign in or to remember your
                preferences, interests, or past items viewed.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Advertising or Targeting Related: We may use first-party or
                third-party cookies and web beacons to deliver content,
                including ads relevant to your interests, on our sites or on
                third-party sites. If you wish to opt-out of these types of
                technologies, you may do so through the AdChoices website or by
                blocking, deleting, or disabling them as your browser or device
                permits. Opting out of these technologies may not necessarily
                mean that you will not see our advertising, but it will prevent
                us from targeting you through the use of cookies, web beacons,
                or related technologies.
              </p>
            </section>
            <section ref={p4El} className="mb-[60px]">
              <Heading
                className="mb-[10px] text-h4Regular"
                level={HeadingLevels.H2}
              >
                4. Use of these technologies by authorized third-party service
                providers:
              </Heading>
              <p className="text-microNormal text-neutral-800">
                Authorized third-party service providers may use cookies, web
                beacons, or similar technologies with our permission to store
                information on our sites, services, applications, and tools.
                These service providers assist us in providing you with a better
                and more secure experience, delivering content, and compiling
                site metrics and analytics. Our confidentiality agreements and
                legal restrictions prevent these service providers from
                collecting your personal information for their own purposes.
                However, third-party cookies are governed by the privacy policy
                of the third party. We do not allow any third-party content on
                our sites, such as item listings, comments, or reviews, to use
                cookies, web beacons, or local storage for tracking purposes or
                to collect personal information, except as authorized by us or
                our service providers. If you suspect any third-party content on
                our sites may be collecting personal information or using
                tracking technologies, please report it to us.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Authorized third-party ad networks and exchanges:
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                We may collaborate with third-party advertising networks and
                exchanges to display ads to you. These providers may use
                third-party cookies, web beacons, or similar technologies to
                collect information, such as your device identifier, IP address,
                or advertising identifier (IDFA). The data they collect may help
                us deliver relevant ads to you on our websites or other
                platforms. Any third-party cookies are subject to the
                third-party provider&apos;s privacy policy.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                To learn more about third-party advertising-related cookies and
                how to opt-out of them, please refer to the following websites:
              </p>
              <div className="text-microNormal text-neutral-800">
                <a
                  href="https://youronlinechoices.eu/"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#0000EE' }}
                >
                  Your Online Choices | EDAA
                </a>
              </div>
              <div className="text-microNormal text-neutral-800">
                <a
                  href="http://www.aboutads.info/choices"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#0000EE' }}
                >
                  http://www.aboutads.info/choices
                </a>
              </div>
              <div className="text-microNormal text-neutral-800">
                <a
                  href="http://www.networkadvertising.org/choices"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#0000EE' }}
                >
                  http://www.networkadvertising.org/choices
                </a>
              </div>
            </section>
          </article>
        </Grid>
      </Wrapper>
    </>
  );
};

export default CookiePolicyTemplate;
